// PreviewTable.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { formatText } from '../common/TextFormatterProvider';
import { formatBooleanValue } from '../../utils/tagValueUtils';
import { isAnyPeriodColumn, formatPeriodKey, periodKeyToDate } from '../../utils/periodUtils';
import {
  getRawCellValue,
  toExcelCellValue,
  SINGLE_VALUE_LEAF,
  SINGLE_VALUE_LABEL,
  applyColumnWidths,
  formatDeclaredDate,
  isDateType,
  buildTableColumnTypeMap,
  getTableLeafType,
} from '../../utils/excelExport';
import { countNoun } from '../../utils/pluralise';
import { encodeDiffLeaf, isDiffLeaf } from '../../utils/diffLeaves';
import useVirtualWindow from '../../hooks/useVirtualWindow';
import useColumnWidths from '../../hooks/useColumnWidths';
import { ColumnWidthGroup, gridBodyStyles, gridHeaderCellStyles } from '../common/tableRendering';

const DELIMITER = '§';

// A tagged diff leaf is a value, not a level of nesting: descending into one would promote its
// verb and operands to header rows. Every walk that builds headers or detects nesting stops at
// one, so a caller can hand a leaf to any column at any depth.
const isNestedValue = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
  && !isDiffLeaf(value)
);

// A cell that carries a value of its own rather than a level of nesting. A column
// holding these in some rows and objects in others gets the reserved single-value
// leaf under its group, so the plain values keep a column instead of vanishing.
const isSingleValue = (value) => (
  value !== null && value !== undefined && value !== '' && !isNestedValue(value)
);

const leafLabel = (key) => (key === SINGLE_VALUE_LEAF ? SINGLE_VALUE_LABEL : formatText(key));

function analyzeColumns(data, columnOrder = null) {
  if (!Array.isArray(data) || data.length === 0) {
    return { columns: {}, maxDepth: 0 };
  }

  // Gather info about each column across all rows
  const columnInfo = {};

  // Get ordered columns with empty ones filtered out
  const orderedColumns = orderColumns(data, columnOrder);

  // For each column, determine the structure and keys
  orderedColumns.forEach(col => {
    let depth = 0; // 0 means no nesting, 1 means single-level, 2 means double-level
    const singleLevelKeys = new Set();
    const doubleLevelKeys = {}; // { firstLevelKey: Set of leafKeys }

    data.forEach(row => {
      const val = row[col];
      if (isNestedValue(val)) {
        Object.keys(val).forEach(subKey => {
          const subVal = val[subKey];
          if (isNestedValue(subVal)) {
            // This indicates double-level nesting
            depth = Math.max(depth, 2);
            if (!doubleLevelKeys[subKey]) {
              doubleLevelKeys[subKey] = new Set();
            }
            Object.keys(subVal).forEach(leafKey => doubleLevelKeys[subKey].add(leafKey));
          } else {
            if (depth < 2) {
              depth = Math.max(depth, 1);
              singleLevelKeys.add(subKey);
            }
          }
        });
      }
    });

    columnInfo[col] = { depth, singleLevelKeys, doubleLevelKeys };
  });

  // Determine global max depth
  let maxDepth = 0;
  Object.values(columnInfo).forEach(info => {
    if (info.depth > maxDepth) {
      maxDepth = info.depth;
    }
  });

  return { columns: columnInfo, maxDepth, orderedColumns };
}

function buildHeaderStructure(data, columnOrder = null, formatColumnLabel = formatMonthKey) {
  if (!Array.isArray(data) || data.length === 0) {
    return { top: [], middle: [], bottom: [], maxDepth: 0, leafOrder: [] };
  }
  
  const { columns, maxDepth, orderedColumns } = analyzeColumns(data, columnOrder);

  // Use the ordered columns instead of computing them here
  const orderedCols = orderedColumns;

  // Helper to get ordered single-level nested keys for column "col"
  const getOrderedSingleLevelKeys = (col) => {
    const keys = [];
    let hasSingleValue = false;
    data.forEach(row => {
      const value = row[col];
      if (isNestedValue(value)) {
        Object.keys(value).forEach(key => {
          if (!keys.includes(key)) {
            keys.push(key);
          }
        });
      } else if (isSingleValue(value)) {
        hasSingleValue = true;
      }
    });
    // Mixed column: the plain values get the first leaf under the group.
    return hasSingleValue ? [SINGLE_VALUE_LEAF, ...keys] : keys;
  };

  // Helper to get ordered double-level keys for column "col"
  const getOrderedDoubleLevelKeys = (col) => {
    const orderedFirstLevel = [];
    const orderedLeafs = {};
    let hasSingleValue = false;
    const singleValueFirstKeys = new Set();
    data.forEach(row => {
      const value = row[col];
      if (isNestedValue(value)) {
        Object.keys(value).forEach(firstKey => {
          if (!orderedFirstLevel.includes(firstKey)) {
            orderedFirstLevel.push(firstKey);
          }
          const subObj = value[firstKey];
          if (isNestedValue(subObj)) {
            if (!orderedLeafs[firstKey]) {
              orderedLeafs[firstKey] = [];
            }
            Object.keys(subObj).forEach(leafKey => {
              if (!orderedLeafs[firstKey].includes(leafKey)) {
                orderedLeafs[firstKey].push(leafKey);
              }
            });
          } else if (isSingleValue(subObj)) {
            singleValueFirstKeys.add(firstKey);
          }
        });
      } else if (isSingleValue(value)) {
        hasSingleValue = true;
      }
    });
    // Mixed at either level: a first-level key that is a group in some rows and a
    // plain value in others keeps a leaf for the plain value; a column that is a
    // plain value in some rows gets one as its first branch.
    singleValueFirstKeys.forEach(firstKey => {
      if (orderedLeafs[firstKey]) orderedLeafs[firstKey].unshift(SINGLE_VALUE_LEAF);
    });
    if (hasSingleValue) orderedFirstLevel.unshift(SINGLE_VALUE_LEAF);
    return { orderedFirstLevel, orderedLeafs };
  };

  const top = [];
  const middle = [];
  const bottom = [];
  const leafOrder = [];

  // Process each top-level column in the order encountered.
  orderedCols.forEach(col => {
    // Use analysis from analyzeColumns, defaulting to depth 0 if missing.
    const analysis = columns[col] || { depth: 0, singleLevelKeys: new Set(), doubleLevelKeys: {} };
    const { depth } = analysis;

    // Format labels - use formatMonthKey for date columns, formatText for others
    const formattedCol = formatColumnLabel(col);

    if (maxDepth === 2) {
      if (depth === 0) {
        // No nesting, rowSpan = 3.
        top.push({
          id: col,
          label: formattedCol,
          colSpan: 1,
          rowSpan: 3,
          depth: 0
        });
        leafOrder.push(col);
      } else if (depth === 1) {
        // Single-level nested.
        const keys = getOrderedSingleLevelKeys(col);
        top.push({
          id: col,
          label: formattedCol,
          colSpan: keys.length || 1,
          rowSpan: 2,
          depth: 1
        });
        keys.forEach(key => {
          bottom.push({
            id: `${col}${DELIMITER}${key}`,
            label: leafLabel(key)
          });
          leafOrder.push(`${col}${DELIMITER}${key}`);
        });
      } else if (depth === 2) {
        // Double-level nested.
        const { orderedFirstLevel, orderedLeafs } = getOrderedDoubleLevelKeys(col);
        const branches = orderedFirstLevel
          .map(firstKey => {
            const isGroup = Object.prototype.hasOwnProperty.call(orderedLeafs, firstKey);
            return { firstKey, leafs: orderedLeafs[firstKey] || [], isGroup };
          })
          .filter(({ leafs, isGroup }) => !isGroup || leafs.length > 0);
        const totalLeafCount = branches.reduce(
          (count, { leafs, isGroup }) => count + (isGroup ? leafs.length : 1),
          0
        );
        if (totalLeafCount === 0) {
          return;
        }
        top.push({
          id: col,
          label: formattedCol,
          colSpan: totalLeafCount,
          rowSpan: 1,
          depth: 2
        });
        branches.forEach(({ firstKey, leafs, isGroup }) => {
          const branchId = `${col}${DELIMITER}${firstKey}`;
          if (!isGroup) {
            middle.push({
              id: branchId,
              label: leafLabel(firstKey),
              colSpan: 1,
              rowSpan: 2
            });
            leafOrder.push(branchId);
            return;
          }

          middle.push({
            id: branchId,
            label: leafLabel(firstKey),
            colSpan: leafs.length,
            rowSpan: 1
          });
          leafs.forEach(leafKey => {
            bottom.push({
              id: `${col}${DELIMITER}${firstKey}${DELIMITER}${leafKey}`,
              label: leafLabel(leafKey)
            });
            leafOrder.push(`${col}${DELIMITER}${firstKey}${DELIMITER}${leafKey}`);
          });
        });
      }
    } else if (maxDepth === 1) {
      if (depth === 0) {
        top.push({
          id: col,
          label: formattedCol,
          colSpan: 1,
          rowSpan: 2,
          depth: 0
        });
        leafOrder.push(col);
      } else if (depth === 1) {
        const keys = getOrderedSingleLevelKeys(col);
        top.push({
          id: col,
          label: formattedCol,
          colSpan: keys.length || 1,
          rowSpan: 1,
          depth: 1
        });
        keys.forEach(key => {
          bottom.push({
            id: `${col}${DELIMITER}${key}`,
            label: leafLabel(key)
          });
          leafOrder.push(`${col}${DELIMITER}${key}`);
        });
      } else {
        // Fallback if a depth=2 column is encountered when maxDepth=1.
        const keys = getOrderedSingleLevelKeys(col);
        top.push({
          id: col,
          label: formattedCol,
          colSpan: keys.length || 1,
          rowSpan: 1,
          depth: 1
        });
        keys.forEach(key => {
          bottom.push({
            id: `${col}${DELIMITER}${key}`,
            label: leafLabel(key)
          });
          leafOrder.push(`${col}${DELIMITER}${key}`);
        });
      }
    } else {
      top.push({
        id: col,
        label: formattedCol,
        colSpan: 1,
        rowSpan: 1,
        depth: 0
      });
      leafOrder.push(col);
    }
  });

  return { top, middle, bottom, maxDepth, leafOrder };
}

// `declaredType` is the leaf's DECLARED schema type where the caller has a
// table schema, or null. Only a declared Date leaf is rendered as a calendar
// day; everything else is shown exactly as stored (M66).
function getCellValue(row, columnId, declaredType = null) {
  const parts = columnId.split(DELIMITER);
  let value = row;
  for (const p of parts) {
    if (p === SINGLE_VALUE_LEAF) {
      // The reserved leaf reads the bare value; an object row has none here.
      if (isNestedValue(value)) return '-';
      break;
    }
    if (value && typeof value === 'object' && !isDiffLeaf(value) && p in value) {
      value = value[p];
    } else {
      return '-';
    }
  }

  // Reached without a renderCell of the caller's own, a leaf still has to read as its verb
  // rather than as its serialised operands.
  if (isDiffLeaf(value)) {
    return encodeDiffLeaf(value, (operand) => formatDiffValue(operand, declaredType));
  }

  // Handle null/undefined/empty
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // A boolean child renders as nothing in React: it is written out as Yes/No.
  if (typeof value === 'boolean') return formatBooleanValue(value);

  // Dates are formatted by DECLARATION, never by value shape.
  if (isDateType(declaredType)) {
    const formattedDate = formatDeclaredDate(value);
    if (formattedDate !== value) return formattedDate;
  }

  const formattedNum = formatNumber(value);
  if (typeof value === 'number' && formattedNum !== value && String(value) !== formattedNum) {
    return formattedNum;
  }
  
  // An object or array the column structure did not descend into: an empty object reads as
  // no value, anything else as its JSON (React cannot render an object).
  if (typeof value === 'object') {
    if (!Array.isArray(value) && Object.keys(value).length === 0) return '-';
    return JSON.stringify(value);
  }

  return value;
}

// Helper function to check if a column is empty
function isColumnEmpty(data, columnName) {
  if (!Array.isArray(data) || data.length === 0) return true;
  
  for (const row of data) {
    const value = row[columnName];
    
    // For non-nested columns
    if (value !== null && value !== undefined && value !== '') {
      return false;
    }
    
    // For nested columns, check all leaf values
    if (isNestedValue(value)) {
      const hasNonEmptyLeaf = checkNestedValues(value);
      if (hasNonEmptyLeaf) return false;
    }
  }
  
  return true;
}

// Recursively check if any leaf value in nested object is non-empty
function checkNestedValues(obj) {
  for (const key in obj) {
    const val = obj[key];
    if (isNestedValue(val)) {
      // Recurse deeper
      if (checkNestedValues(val)) return true;
    } else {
      // Leaf value
      if (val !== null && val !== undefined && val !== '') {
        return true;
      }
    }
  }
  return false;
}

// The flat columns' order. Rows arrive as Firestore maps, whose key order is
// not guaranteed, so a caller that has an order names it: 'alphabetical', or
// the list of column names to follow (anything not listed keeps its first-seen
// place after them). Nested period columns keep their date order regardless.
const NAME_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const applyColumnOrder = (columns, columnOrder) => {
  if (columnOrder === 'alphabetical') return [...columns].sort(NAME_COLLATOR.compare);
  if (Array.isArray(columnOrder)) {
    const rank = new Map(columnOrder.map((name, index) => [name, index]));
    const listed = columns.filter((col) => rank.has(col)).sort((a, b) => rank.get(a) - rank.get(b));
    const unlisted = columns.filter((col) => !rank.has(col));
    return [...listed, ...unlisted];
  }
  return columns;
};

// Order columns according to requirements
function orderColumns(data, columnOrder = null) {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  // Get all unique columns across all rows
  const allColumns = new Set();
  data.forEach(row => {
    Object.keys(row).forEach(col => allColumns.add(col));
  });
  
  // Filter out empty columns
  const nonEmptyColumns = Array.from(allColumns).filter(col => !isColumnEmpty(data, col));
  
  // Separate non-nested and nested columns
  const nonNestedColumns = [];
  const nestedColumns = [];
  
  nonEmptyColumns.forEach(col => {
    let isNested = false;
    
    // Check if this column has nested values in any row
    for (const row of data) {
      const value = row[col];
      if (isNestedValue(value)) {
        isNested = true;
        break;
      }
    }
    
    if (isNested) {
      nestedColumns.push(col);
    } else {
      nonNestedColumns.push(col);
    }
  });
  
  // Sort nested columns by date if possible
  const dateColumns = [];
  const nonDateColumns = [];
  
  nestedColumns.forEach(col => {
    const date = periodKeyToDate(col);
    if (date) {
      dateColumns.push({ name: col, date });
    } else {
      nonDateColumns.push(col);
    }
  });
  
  // Sort date columns ascending
  dateColumns.sort((a, b) => a.date - b.date);
  
  // Combine: non-nested first, then sorted date columns, then other nested columns
  return [
    ...applyColumnOrder(nonNestedColumns, columnOrder),
    ...dateColumns.map(dc => dc.name),
    ...nonDateColumns
  ];
}

// Format period key for display (supports all period formats)
const formatMonthKey = (column) => {
  // Use the period utility to format any period column
  if (typeof column === 'string' && isAnyPeriodColumn(column)) {
    return formatPeriodKey(column);
  }
  return formatText(column);
};

// Format numbers with commas and 2 decimal places
const formatNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value;
};

// The same date and number conventions as a plain cell, but for a value being written into a
// longer string rather than returned as the whole cell. Exported so a caller encoding its own
// cells reads its values exactly as the table beside them does. Dates are formatted only when
// the caller says the column is DECLARED Date; otherwise the text is preserved (M66).
export const formatDiffValue = (value, declaredType = null) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return formatBooleanValue(value);
  if (isDateType(declaredType)) {
    const formattedDate = formatDeclaredDate(value);
    if (formattedDate !== value) return formattedDate;
  }
  return String(formatNumber(value));
};

// One row, memoised so a window shift only renders the rows that actually entered the
// window; retained rows keep every prop referentially stable. The row and its cells carry
// the class names gridBodyStyles (tableRendering.js) styles once for the whole body.
const PreviewTableRow = React.memo(function PreviewTableRow({ row, rowIndex, rowRef, leafOrder, cellText, renderCell }) {
  return (
    <TableRow hover ref={rowRef} className={rowIndex % 2 === 0 ? 'body-row striped-row' : 'body-row'}>
      {leafOrder.map((colId) => {
        // A caller that draws its own cells (the diff view's coloured verbs, each with its
        // own tooltip) gets first refusal on every one of them, against the raw value at
        // that column's path.
        const custom = renderCell ? renderCell(getRawCellValue(row, colId, DELIMITER), row, colId) : undefined;
        if (custom !== undefined) {
          return <td key={colId} className="data-cell">{custom}</td>;
        }
        // Anything it declines shows the table's own text, in full on hover for a value cut
        // off by the column width.
        const text = cellText(row, colId);
        return <td key={colId} className="data-cell" title={text}>{text}</td>;
      })}
    </TableRow>
  );
});

// `columnOrder`: 'alphabetical', a list of column names, or null for first-seen
// order — see applyColumnOrder. The export follows the same order.
// `tableSchema`: the Firestore table doc, where the caller has one. It supplies
// the DECLARED column types: only a declared Date leaf is rendered (and
// exported) as a date; without a schema every value is shown as stored (M66).
// `truncated`: the rows are a sample of a larger result, so the footer counts
// "sample rows" rather than presenting the sample as the whole (M230).
// `maxHeight`: the most the inline grid grows to before it scrolls, for a grid whose
// parent gives it no height (a chat thread); without it the grid fills its parent.
// `expandedZIndex`: the stacking of the expanded view's dialog, for a grid shown inside
// raised layers (the agent chat).
function PreviewTable({ data, exportHeaders, renderCell, filename = null, columnOrder = null, tableSchema = null, truncated = false, formatColumnLabel = null, maxHeight = null, expandedZIndex = null }) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const processedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data;
  }, [data]);

  // Windowed scrolling: only ~100 rows are mounted, framed by two spacer rows
  // sized so the scrollbar reflects the full dataset.
  const {
    start: windowStart,
    end: windowEnd,
    topPad,
    bottomPad,
    attachContainer,
    measureRow,
    resetWindow,
  } = useVirtualWindow(processedData.length, { defaultRowHeight: 37 });

  const windowRows = useMemo(() => {
    return processedData.slice(windowStart, windowEnd);
  }, [processedData, windowStart, windowEnd]);

  useEffect(() => {
    resetWindow();
  }, [processedData, resetWindow]);

  // Keyed on the order's content, not its identity: a caller recomputing its
  // column list each render must not rebuild the header each render.
  const columnOrderKey = Array.isArray(columnOrder) ? columnOrder.join('\u0000') : (columnOrder || '');
  const headerStructure = useMemo(() => {
    return buildHeaderStructure(processedData, Array.isArray(columnOrder)
      ? columnOrderKey.split('\u0000')
      : (columnOrder || null), formatColumnLabel || formatMonthKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedData, columnOrderKey, formatColumnLabel]);
  const { top, middle, bottom, leafOrder } = headerStructure;

  const tableColumnTypeMap = useMemo(() => buildTableColumnTypeMap(tableSchema), [tableSchema]);
  const typeForLeaf = useCallback((colId) => (
    tableSchema ? getTableLeafType(tableSchema, tableColumnTypeMap, colId, DELIMITER) : null
  ), [tableSchema, tableColumnTypeMap]);

  // The text a cell shows by the table's own formatting: what every cell a caller does not
  // draw itself displays, and what the column widths are measured from (a caller's own
  // drawing of a diff leaf shows this same text). Stable identity (useCallback) so the
  // memoised PreviewTableRow can skip re-rendering retained rows on window shifts.
  const cellText = useCallback((row, colId) => (
    getCellValue(row, colId, typeForLeaf(colId))
  ), [typeForLeaf]);

  const columnWidths = useColumnWidths({
    rows: processedData,
    leafOrder,
    headerRows: headerStructure,
    getCellText: cellText,
  });
  const bodyStyles = useMemo(() => gridBodyStyles(theme), [theme]);
  const headerCellBase = useMemo(() => gridHeaderCellStyles(theme), [theme]);

  const exportToExcel = () => {
    if (!Array.isArray(processedData) || processedData.length === 0) return;

    // Build Array-of-Arrays export to allow inserting header rows
    const exportData = [];

    // Optional: prepend custom header rows (key/value pairs)
    if (exportHeaders && typeof exportHeaders === 'object') {
      const bannedKeys = new Set(['startDate', 'endDate', 'start_date', 'end_date']);
      Object.entries(exportHeaders).forEach(([key, val]) => {
        if (bannedKeys.has(key)) return;
        let outVal = val;
        if (Array.isArray(outVal)) {
          outVal = outVal.join(', ');
        } else if (outVal === null || outVal === undefined) {
          outVal = '';
        } else if (typeof outVal === 'object') {
          outVal = JSON.stringify(outVal);
        }
        exportData.push([formatText(key), outVal]);
      });
    }

    // Header row (leaf labels)
    const headerRow = leafOrder.map(leafId => leafId.split(DELIMITER).map((key, index) => (
      index === 0 && formatColumnLabel ? formatColumnLabel(key) : leafLabel(key)
    )).join(' > '));
    exportData.push(headerRow);

    // Data rows
    processedData.forEach(row => {
      const dataRow = leafOrder.map(colId => {
        const rawValue = getRawCellValue(row, colId, DELIMITER);
        const declaredType = typeForLeaf(colId);
        // A leaf's verb is part of what the cell says, so the workbook carries the same text
        // the screen does rather than the leaf's serialised operands.
        if (isDiffLeaf(rawValue)) {
          return encodeDiffLeaf(rawValue, (operand) => formatDiffValue(operand, declaredType));
        }
        return toExcelCellValue(rawValue, declaredType);
      });
      exportData.push(dataRow);
    });

    // Create worksheet and workbook from AoA
    const worksheet = applyColumnWidths(XLSX.utils.aoa_to_sheet(exportData, { UTC: true, dateNF: 'dd/mm/yyyy' }), exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    XLSX.writeFile(workbook, `${filename || 'table-data'}.xlsx`);
  };

  if (!Array.isArray(processedData) || processedData.length === 0) {
    return (
      <Typography variant="body1" align="center" sx={{ p: 2, color: 'text.secondary' }}>
        No data available
      </Typography>
    );
  }

  const renderHeaderRow = (headers) => (
    <TableRow>
      {headers.map(header => (
        <TableCell
          key={header.id}
          colSpan={header.colSpan || 1}
          rowSpan={header.rowSpan || 1}
          align="center"
          title={header.label}
          sx={headerCellBase}
        >
          {header.label}
        </TableCell>
      ))}
    </TableRow>
  );

  // The grid, drawn inline or, when expanded, in the dialog: never both at once, so the one
  // scroll window follows whichever is showing. The whole header block is pinned rather
  // than each header cell, so its two or three rows stay stacked in order while the body
  // scrolls beneath them.
  const renderGrid = (containerMaxHeight) => (
    <TableContainer ref={attachContainer} sx={{ flexGrow: 1, overflow: 'auto', maxHeight: containerMaxHeight }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <ColumnWidthGroup leafOrder={leafOrder} columnWidths={columnWidths} />
        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
          {top.length > 0 && renderHeaderRow(top)}
          {middle.length > 0 && renderHeaderRow(middle)}
          {bottom.length > 0 && renderHeaderRow(bottom)}
        </TableHead>
        <TableBody sx={bodyStyles}>
          {topPad > 0 && (
            <TableRow aria-hidden="true">
              <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: topPad }} />
            </TableRow>
          )}
          {windowRows.map((row, windowIndex) => (
            <PreviewTableRow
              key={windowStart + windowIndex}
              row={row}
              rowIndex={windowStart + windowIndex}
              rowRef={windowIndex === 0 ? measureRow : undefined}
              leafOrder={leafOrder}
              cellText={cellText}
              renderCell={renderCell}
            />
          ))}
          {bottomPad > 0 && (
            <TableRow aria-hidden="true">
              <TableCell colSpan={leafOrder.length} padding="none" sx={{ border: 'none' }} style={{ height: bottomPad }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const footer = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
      <Tooltip title="Export to Excel">
        <IconButton
          onClick={exportToExcel}
          size="medium"
          sx={{
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            ml: 1,
            borderRadius: '8px',
            padding: '8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: 'primary.main',
              borderColor: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Tooltip>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', pr: 1 }}>
        {countNoun(processedData.length, truncated ? 'sample row' : 'row')}
      </Typography>
    </Box>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderBottom: 1, borderColor: 'divider', p: 1 }}>
          <Tooltip title="Expand">
            <IconButton onClick={() => setIsExpanded(true)} size="small" sx={{ color: 'text.secondary' }}>
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {!isExpanded && renderGrid(maxHeight)}
        {!isExpanded && footer}
      </Paper>

      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        fullWidth
        maxWidth={false}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        sx={expandedZIndex ? { zIndex: expandedZIndex } : undefined}
        PaperProps={{ sx: { width: '95vw', height: '90vh' } }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <IconButton onClick={() => setIsExpanded(false)} size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2000 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            {renderGrid(null)}
            {footer}
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PreviewTable;
